'use client'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { trackCtaClicked } from '../../lib/analytics'

const FOUNDERS_GUIDE_SLUGS = new Set(['o-1-visa-roadmap', 'l-1-visa-roadmap'])

interface GuideConsultationCtaProps {
  guideSlug?: string
  visaType?: string | null
}

function getCtaCopy(visaType?: string | null) {
  if (!visaType) {
    return {
      heading: 'Need Personalized Guidance?',
      body: 'Our experienced immigration attorneys can guide you through every step of the process. Schedule a consultation to discuss your specific situation.',
    }
  }

  return {
    heading: `Talk to an Attorney About Your ${visaType} Case`,
    body: `Our immigration attorneys help individuals and employers navigate ${visaType} visas — from eligibility through approval. Schedule a consultation to discuss your specific situation.`,
  }
}

export function GuideConsultationCta({ guideSlug, visaType }: GuideConsultationCtaProps) {
  const { heading, body } = getCtaCopy(visaType)
  const showFoundersLink = guideSlug ? FOUNDERS_GUIDE_SLUGS.has(guideSlug) : false

  return (
    <div className="mt-12 space-y-6">
      {showFoundersLink && (
        <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 md:p-8">
          <h3 className="text-xl font-serif text-brand-blue mb-2">
            Immigration for Startup Founders
          </h3>
          <p className="text-gray-700 mb-4">
            Building a company? See how we help founders with O-1, EB-1, E-2, H-1B, and L-1
            immigration strategy.
          </p>
          <Link
            href="/founders"
            onClick={() =>
              trackCtaClicked({
                cta_text: 'Immigration for startup founders',
                cta_location: 'guide_detail',
                destination_url: '/founders',
              })
            }
            className="inline-flex items-center gap-2 text-brand-blue font-medium hover:text-brand-gold transition-colors"
          >
            Immigration for startup founders
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      )}

      <div className="bg-brand-blue rounded-2xl p-8 md:p-12 text-center">
        <h3 className="text-2xl md:text-3xl font-serif text-white mb-4">{heading}</h3>
        <p className="text-white/80 mb-8 max-w-xl mx-auto">{body}</p>
        <a
          href="/consultation"
          onClick={() =>
            trackCtaClicked({
              cta_text: 'Request a Consultation',
              cta_location: 'guide_detail',
              destination_url: '/consultation',
            })
          }
          className="inline-flex items-center gap-2 px-8 py-3 bg-white text-primary rounded-full hover:bg-brand-gold-light transition-colors font-medium"
        >
          Request a Consultation
          <ChevronRight className="w-5 h-5" />
        </a>
      </div>
    </div>
  )
}
